<script setup>
import { useAuthStore } from '@/stores/authStore';
import { useProductStore } from '@/stores/productsStore';
import { useOrderStore } from '@/stores/OrderStore';
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { useToast } from 'vue-toastification';
import { useKeyboardStore } from '@/stores/keyboardStore';
import { useErrorLogger } from '@/composables/useErrorLogger';
import { isOnline } from '@/services/healthCheck';
import { hasPositivePrice, pickScanProduct } from '@/services/priceGuard';
import axios from 'axios';

const toast = useToast();
const authStore = useAuthStore();
const store = useProductStore();
const orderStore = useOrderStore();
const keyboardStore = useKeyboardStore();
const { logError } = useErrorLogger();
const searchQuery = ref('');
const parsedSkuData = ref(null);

// Function to get cached product from localStorage
const getCachedProduct = (barcode) => {
  const cachedProducts = JSON.parse(localStorage.getItem('cachedProducts') || '{}');
  return cachedProducts[barcode];
};

// Function to cache product in localStorage
const cacheProduct = (barcode, product) => {
  // Never cache an unpriced product — the cache is preferred on future scans and
  // a 0-price entry would keep resolving at $0 until the next catalog regen.
  if (!hasPositivePrice(product)) return;
  const cachedProducts = JSON.parse(localStorage.getItem('cachedProducts') || '{}');
  cachedProducts[barcode] = product;
  localStorage.setItem('cachedProducts', JSON.stringify(cachedProducts));
};

// Function to handle API request
const fetchProductByBarcode = async (query) => {
    if (!query || query.length < 3) return;

    // Check delete marker first — a deleted product must not be scannable
    const cachedProduct = getCachedProduct(query);
    if (cachedProduct?._deleted) {
        toast.error('Product Not Found!');
        searchQuery.value = '';
        return;
    }

    const existingProduct = store.products.find(p =>
        !p._deleted && p.Barcodes && p.Barcodes.some(bc => (typeof bc === 'string' ? bc : bc?.Barcode) === query)
    );

    if (existingProduct) {
        // Prefer the cached overlay if present (has realtime price/name updates) —
        // but only when it carries a usable price; a 0-price cache entry must not
        // shadow the catalog price.
        orderStore.addOrderItem(pickScanProduct(cachedProduct, existingProduct));
        toast.success('Product found locally');
        searchQuery.value = '';
        return;
    }

    // Then check localStorage cache (products previously fetched from API).
    // Unpriced cache entries are skipped so the server lookup can resolve a
    // secondary-price-list price (or, offline, the cashier sets the price manually)
    // instead of adding the item at $0.
    if (cachedProduct && hasPositivePrice(cachedProduct)) {
        orderStore.addOrderItem(cachedProduct);
        toast.success('Product loaded from cache');
        searchQuery.value = '';
        return;
    }

    // Offline: the server lookup can't run — add it as an editable manual line instead of
    // failing. The cashier sets the SKU + price in the cart; ERP matches it on sync.
    if (!isOnline.value) {
        orderStore.addOfflineManualItem(query);
        toast.info(`"${query}" not found — can't search online items while offline. Add the SKU & price manually in the cart.`);
        searchQuery.value = '';
        return;
    }

    // Finally try API
    authStore.isBarcodeSearchFromBackend = true;
    try {
        const response = await axios.get(
            `/api/Order/GetProduct?barcode=${query}`,
            {
                headers: {
                    'store-id': authStore.branchId,
                    'Authorization': `Bearer ${authStore.managerToken ? authStore.managerToken : authStore.token}`
                }
            }
        );
        
        if (response.data.Status === 200 && response.data.Product) {
            // Cache the product for future use
            cacheProduct(query, response.data.Product);
            
            orderStore.addOrderItem(response.data.Product); 
            toast.success('Product loaded from server');
            searchQuery.value = '';
        } else {
            toast.error('Product Not Found!');
        }
    } catch (error) {
        // Couldn't reach the server (no HTTP response = offline / backend down) → fall back
        // to an editable manual line instead of failing, even if isOnline hadn't flipped yet.
        if (!error.response) {
            orderStore.addOfflineManualItem(query);
            toast.info(`"${query}" not found — can't search online items while offline. Add the SKU & price manually in the cart.`);
            searchQuery.value = '';
            return;
        }
        console.error('API error:', error);
        toast.error('Failed to search for product');
        logError({ callStack: error?.stack || String(error), errorDetails: { message: error?.message, httpStatus: error?.response?.status, component: 'BarcodeInput', action: 'fetchProductByBarcode', query, branchId: authStore.branchId } });
    } finally {
        authStore.isBarcodeSearchFromBackend = false;
    }
};

const mapBackendProduct = (rawProduct) => {
    if (!rawProduct) return null;
    return {
        Barcodes: rawProduct.Barcodes,
        Sku: rawProduct.Sku,
        RetailSku: rawProduct.RetailSku,
        Name: rawProduct.ItemName || rawProduct.Name,
        ImageUrl: rawProduct.ImageUrl,
        MaxQty: rawProduct.Quantity ?? rawProduct.MaxQty,
        Price: rawProduct.Price,
        isNonStocking: rawProduct.NonStocking,
        Comment: rawProduct.Comment,
    };
};

const findProductLocallyBySku = (sku) => {
    const normalized = sku?.trim().toLowerCase();
    if (!normalized) return null;

    return store.products.find((p) => {
        if (p?._deleted) return false;
        const skuMatch = p?.Sku?.toLowerCase() === normalized;
        const retailMatch = p?.RetailSku?.toLowerCase() === normalized;
        const barcodeMatch = Array.isArray(p?.Barcodes) && p.Barcodes.some((bc) => (typeof bc === 'string' ? bc : bc?.Barcode)?.toLowerCase() === normalized);
        return skuMatch || retailMatch || barcodeMatch;
    }) || null;
};

const fetchProductBySkuBackend = async (sku) => {
    const token = authStore.managerToken || authStore.token;
    const branchId = authStore.branchId;
    if (!token) return null;

    const headers = {
        'store-id': branchId,
        'Authorization': `Bearer ${token}`,
    };

    try {
        const response = await axios.get(
            `/api/Order/GetProductByRetailSkuSingle?retailSku=${sku}`,
            { headers }
        );

        if (response.data?.Status === 200 && response.data?.Product) {
            return mapBackendProduct(response.data.Product);
        }
    } catch (error) {
        console.error('Backend fetch by SKU error:', error);
        logError({ callStack: error?.stack || String(error), errorDetails: { message: error?.message, httpStatus: error?.response?.status, component: 'BarcodeInput', action: 'fetchProductBySkuBackend', sku, branchId } });
    }

    return null;
};

const resolveProductFromParsedSku = async (parsedSku) => {
    if (!parsedSku) return null;

    // Check delete marker — treat deleted product as not found
    const cached = getCachedProduct(parsedSku);
    if (cached?._deleted) return null;

    const localProduct = findProductLocallyBySku(parsedSku);
    if (localProduct) return cached ?? localProduct;

    if (cached) return cached;

    const backend = await fetchProductBySkuBackend(parsedSku);
    if (backend) {
        cacheProduct(parsedSku, backend);
        return backend;
    }

    return null;
};

const fetchEmbeddedBSURItem = async (query) => {
    const token = authStore.managerToken ? authStore.managerToken : authStore.token;
    if (!token) {
        console.log('Authentication token is missing. Please log in again.');
        return;
    }
    if (!query || query.length < 3) return;

    // Offline: the embedded-barcode parse + product resolve are server calls — add an
    // editable manual line instead (cashier sets SKU + price; ERP matches on sync).
    if (!isOnline.value) {
        orderStore.addOfflineManualItem(query);
        toast.info(`"${query}" not found — can't search online items while offline. Add the SKU & price manually in the cart.`);
        searchQuery.value = '';
        return;
    }

    authStore.isBarcodeSearchFromBackend = true;
    try {
        const response = await axios.get(
            `/api/Barcode/parse?barcode=${query}`,
            {
                headers: {
                    'store-id': authStore.branchId,
                    'Authorization': `Bearer ${token}`
                }
            }
        );
        
        if (response.data.isValid === true) {
            parsedSkuData.value = {
                sku: response.data.sku,
                price: response.data.price
            };

            const isP2 = query.toUpperCase().startsWith('P2');

            // Try to resolve full product details using the parsed SKU
            const resolved = await resolveProductFromParsedSku(response.data.sku);

            if (resolved) {
                // Ensure price is present; fall back to parsed price when missing
                const productToAdd = {
                    ...resolved,
                    Price: response.data.price,
                    price: response.data.price,
                    storedBarcodeCode: isP2 ? query.toUpperCase() : null,
                };
                orderStore.addOrderItem(productToAdd);
                toast.success('BSUR Product loaded');
            } else {
                // Fallback: add minimal item using parsed data
                orderStore.addOrderItem({
                    Barcodes: [query],
                    Sku: response.data.sku,
                    RetailSku: response.data.sku,
                    Name: `BSUR Item`,
                    ImageUrl: '',
                    MaxQty: 99,
                    Price: response.data.price,
                    isNonStocking: true,
                    Comment: '',
                    storedBarcodeCode: isP2 ? query.toUpperCase() : null,
                });
                toast.success('BSUR Product added (fallback)');
            }
            searchQuery.value = '';
        } else {
            toast.error('BSUR Product Not Found!');
        }
    } catch (error) {
        // Server unreachable (offline / backend down) → fall back to a manual line.
        if (!error.response) {
            orderStore.addOfflineManualItem(query);
            toast.info(`"${query}" not found — can't search online items while offline. Add the SKU & price manually in the cart.`);
            searchQuery.value = '';
            return;
        }
        console.error('API error:', error);
        toast.error('Failed to search for BSUR product');
        logError({ callStack: error?.stack || String(error), errorDetails: { message: error?.message, httpStatus: error?.response?.status, component: 'BarcodeInput', action: 'fetchEmbeddedBSURItem', query, branchId: authStore.branchId } });
    } finally {
        authStore.isBarcodeSearchFromBackend = false;
    }
};

// Watch for barcode search changes
watch(searchQuery, (newQuery) => {
    const query = newQuery.trim();
    if (!query) return;
});

// Computed property for filtered products (purely local)
const filteredProducts = computed(() => {
    if (!store.products || !searchQuery.value) {
        return []; 
    }

    const query = searchQuery.value.toLowerCase().trim();
    if (!query) return [];

    // Create a Map to store products by unique SKUs
    const productMap = new Map();

    // Filter products by Barcodes and add them to the Map (unique SKUs only)
    store.products.forEach(product => {
        if (!product._deleted && product.Barcodes && product.Barcodes.some(barcode => (typeof barcode === 'string' ? barcode : barcode?.Barcode)?.toLowerCase() === query)) {
            productMap.set(product.Sku, product);
        }
    });

    // Filter products by RetailSku and add them to the Map (unique SKUs only)
    store.products.forEach(product => {
        if (!product._deleted && product.RetailSku && product.RetailSku.toLowerCase() === query) {
            if (!productMap.has(product.Sku)) {
                productMap.set(product.Sku, product);
            }
        }
    });

    // // Filter products by Name and add them to the Map (ensuring unique SKUs)
    // store.products.forEach(product => {
    //     if (product.Name && product.Name.toLowerCase().includes(query)) {
    //         if (!productMap.has(product.Sku)) {
    //             productMap.set(product.Sku, product);
    //         }
    //     }
    // });

    let results = Array.from(productMap.values());

    // Prioritize misc item for inputs 2, 22, 222
    const miscQueries = ['2', '22', '222'];
    if (miscQueries.includes(query)) {
        const miscItem = store.products.find(p => p.Sku === '2');
        if (miscItem) {
            results = results.filter(p => p.Sku !== '2');
            results.unshift(miscItem);
        }
    }

    // Prioritize tip item for inputs 9, 99, 999
    const tipQueries = ['9', '99', '999'];
    if (tipQueries.includes(query)) {
        const tipItem = store.products.find(p => p.Sku === 'TIP-99');
        if (tipItem) {
            results = results.filter(p => p.Sku !== 'TIP-99');
            results.unshift(tipItem);
        }
    }

    return results.slice(0, 5);
});

function clearSearch() {
    searchQuery.value = '';
}

function handleParsedSkuClose() {
    authStore.isParsedSkuPopupVisible = false;
    parsedSkuData.value = null;
}

function handleClick(product) {
    if (!orderStore.addOrderItem(product)) return;
    toast.success('Item added to order');
    searchQuery.value = '';
}

// Keyboard shortcuts
const searchInput = ref(null);

// watch for loginns
watch(
  [() => authStore.isUserLoggedIn, () => authStore.isManagerLoggedIn],
  ([userLoggedIn, managerLoggedIn]) => {
    if (userLoggedIn) {
      nextTick(() => searchInput.value?.focus());
    }
    if (managerLoggedIn) {
      setAttribute();  
      nextTick(() => searchInput.value?.focus());
    }
    if(!managerLoggedIn) {
        removeAttribute();
    }
  }
);

// watch when order items are null
watch(
  () => orderStore.state.orderItems,
  (newItems) => {
    if (newItems.length === 0) {
      searchQuery.value = '';
      nextTick(() => searchInput.value?.focus());
    }
  },
  { deep: true }
);

onMounted(() => {
  if (authStore.isUserLoggedIn || authStore.isManagerLoggedIn) {
    nextTick(() => searchInput.value?.focus());
  }
  keyboardStore.register('Alt+B', () => searchInput.value?.focus());
});

onUnmounted(() => {
  keyboardStore.unregister('Alt+B');
});

function setAttribute () {
    searchInput.value.setAttribute("autocomplete", "off");
}

function removeAttribute () {
    searchInput.value.removeAttribute("autocomplete");
}


// Handle search item by skuu

const minCharacters = 5;

const responseData = ref([]);
const expectedProductCount = ref(0);
const isRequsetBeingMade = ref(false);

const fetchProductByRetailSku = async (query) => {
  if (!query) {
    toast.error('Please enter a valid SKU');
    return;
  }

  // Load from cached products if available
  const cachedProducts = JSON.parse(localStorage.getItem('cachedProducts') || '{}');

  // Direct key lookup catches delete markers written under RetailSku
  if (cachedProducts[query]?._deleted) {
    toast.error('Product Not Found!');
    searchQuery.value = '';
    return;
  }

  const cachedProduct = Object.values(cachedProducts).find(
    product => !product._deleted && product.RetailSku === query && hasPositivePrice(product)
  );

  if (cachedProduct) {
    orderStore.addOrderItem(cachedProduct);
    searchQuery.value = '';
    return;
  }

  // Offline: SKU lookup is a server call — add an editable manual line instead.
  if (!isOnline.value) {
    orderStore.addOfflineManualItem(query);
    toast.info(`"${query}" not found — can't search online items while offline. Add the SKU & price manually in the cart.`);
    searchQuery.value = '';
    return;
  }

  isRequsetBeingMade.value = true;
  responseData.value = [];
  expectedProductCount.value = 0;

  authStore.isRetailSkuSearchFromBackend = true;

  try {
    const response = await axios.get(
      `/api/Order/GetProductByRetailSkuSingle?retailSku=${query}`,
      {
        headers: {
          'store-id': authStore.branchId,
          'Authorization': `Bearer ${authStore.managerToken || authStore.token}`,
        }
      }
    );

    const product = response.data.Product || [];

    if (response.data.Status === 200) {
      expectedProductCount.value = product.length;
      responseData.value = product;
      toast.success('Product loaded from server');

      const rawProduct = product;
      const mappedProduct = {
        Barcodes: rawProduct.Barcodes,
        Sku: rawProduct.Sku,
        RetailSku: rawProduct.RetailSku,
        Name: rawProduct.ItemName,
        ImageUrl: rawProduct.ImageUrl,
        MaxQty: rawProduct.Quantity,
        Price: rawProduct.Price,
        // Discount: rawProduct.DiscountPrice,
        isNonStocking: rawProduct.NonStocking,
        Comment: rawProduct.Comment,
      };

      orderStore.addOrderItem(mappedProduct);
      cacheProduct(query, mappedProduct);
      searchQuery.value = '';
    } else {
      toast.error('Product Not Found!');
    }

  } catch (error) {
    // Server unreachable (offline / backend down) → fall back to a manual line.
    if (!error.response) {
      orderStore.addOfflineManualItem(query);
      toast.info(`"${query}" not found — can't search online items while offline. Add the SKU & price manually in the cart.`);
      searchQuery.value = '';
      return;
    }
    console.error('Error fetching product:', error);
    toast.error('Error fetching product');
    logError({ callStack: error?.stack || String(error), errorDetails: { message: error?.message, httpStatus: error?.response?.status, component: 'BarcodeInput', action: 'fetchProductByRetailSku', query, branchId: authStore.branchId } });
  } finally {
    isRequsetBeingMade.value = false;
    authStore.isRetailSkuSearchFromBackend = false;
  }
};


function handleEnterKey() {
    if (orderStore.isThisPaidManualOrder) {
        toast.error('This manual order is already paid and cannot be edited.');
        searchQuery.value = '';
        return;
    }

    const query = searchQuery.value.trim();
    
    const isSearchQueryOnlyNumbers = /^\d+$/.test(query);
    
    if(isSearchQueryOnlyNumbers) {
        // First try to find locally
        if (filteredProducts.value.length > 0) {
            const firstProduct = filteredProducts.value[0];
            handleClick(firstProduct);
        } 
        // If not found locally, try cache or API
        else if (query.length >= 3) {
            fetchProductByBarcode(query);
        }
    } else {
        if (query.length < minCharacters) {
          toast.error(`Please enter at least ${minCharacters} characters to search the full catalog.`);
          return;
        }

        if (query) {
          if (query.toUpperCase().startsWith('P1') || query.toUpperCase().startsWith('P2')) {
                fetchEmbeddedBSURItem(query);
            } else {
                fetchProductByRetailSku(query);
            }
        }
    }
}

</script>

<template>
    <div class="relative w-full">
        <input 
            ref="searchInput"
            autocomplete="off"
            v-model="searchQuery" 
            :disabled="!(authStore.isUserLoggedIn || authStore.isManagerLoggedIn) || orderStore.isThisPaidManualOrder" 
            type="text"
            name="barcode-search" 
            id="barcode-search"
            class="bg-gray-100 border-0 outline-none p-2 rounded-lg w-full pr-10"
            :class="{ 'opacity-50 cursor-not-allowed': !(authStore.isUserLoggedIn || authStore.isManagerLoggedIn) || orderStore.isThisPaidManualOrder }"
            :placeholder="orderStore.isThisPaidManualOrder ? 'Paid manual order loaded - items cannot be added' : 'Scan or Enter Barcode, SKU, or Item Name'"
            @keydown.enter="handleEnterKey"
        >

        <!-- Clear Button (X) -->
        <button v-if="searchQuery" @click="clearSearch"
            class="absolute right-2 top-2 text-gray-500 hover:text-gray-700">
            <i class="pi pi-times"></i>
        </button>
    </div>

    <!-- <div v-if="searchQuery.value && filteredProducts.length === 0">
        <p>No products found.</p>
    </div>

    <div v-else-if="filteredProducts.length > 0"
        class="absolute top-full left-0 overflow-y-auto w-full bg-white shadow-xl rounded-2xl z-50"
        style="max-height: 620px;">
        <table class="min-w-full bg-white border border-gray-200">
            <thead>
                <tr class="border-b bg-gray-100">
                    <th class="py-2 px-4 text-left text-gray-600 font-medium">Image</th>
                    <th class="py-2 px-4 text-left text-gray-600 font-medium">Item Name</th>
                    <th class="py-2 px-4 text-left text-gray-600 font-medium">Retail Sku</th>
                    <th class="py-2 px-4 text-left text-gray-600 font-medium">QOH Qty</th>
                    <th class="py-2 px-4 text-left text-gray-600 font-medium">Price</th>
                </tr>
            </thead>
            <tbody>
                <tr @click="handleClick(product)" v-for="product in filteredProducts" :key="product.Sku"
                    class="border-t cursor-pointer hover:bg-gray-50">
                    <td class="py-1 px-4">
                        <img :src="`${product.ImageUrl}`"
                            alt="Product Image" class="w-16 h-16 object-cover">
                    </td>
                    <td class="py-1 px-4">{{ product.Name }}</td>
                    <td class="py-1 px-4">{{ product.RetailSku }}</td>
                    <td class="py-1 px-4">{{ product.MaxQty }}</td>
                    <td class="py-1 px-4">{{ product.Price }}</td>
                </tr>
            </tbody>
        </table>
    </div> -->
</template>


<style scoped>

</style>
